import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { detectUserCurrency } from '../utils/CurrencyUtils';

interface CurrencyContextType {
  detectedCurrency: string;
  isDetecting: boolean;
  detectCurrency: (force?: boolean) => Promise<void>;
  clearCurrencyCache: () => void;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

interface CurrencyProviderProps {
  children: ReactNode;
}

const CURRENCY_CACHE_KEY = 'detected_user_currency';
const CURRENCY_CACHE_TIMESTAMP_KEY = 'detected_user_currency_timestamp';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours
// For testing: use shorter cache (5 minutes) by changing to: 5 * 60 * 1000

export const CurrencyProvider: React.FC<CurrencyProviderProps> = ({ children }) => {
  const [detectedCurrency, setDetectedCurrency] = useState<string>('USD');
  const [isDetecting, setIsDetecting] = useState<boolean>(true);

  const detectCurrency = async (force: boolean = false) => {
    // Check cache first (unless forced)
    if (!force) {
      try {
        const cachedCurrency = localStorage.getItem(CURRENCY_CACHE_KEY);
        const cachedTimestamp = localStorage.getItem(CURRENCY_CACHE_TIMESTAMP_KEY);
        
        if (cachedCurrency && cachedTimestamp) {
          const timestamp = parseInt(cachedTimestamp, 10);
          const now = Date.now();
          
          // Use cached currency if it's still valid (within 24 hours)
          if (now - timestamp < CACHE_DURATION) {
            console.log('💰 Using cached currency:', cachedCurrency);
            setDetectedCurrency(cachedCurrency);
            setIsDetecting(false);
            return;
          }
        }
      } catch (error) {
        console.warn('Error reading currency cache:', error);
      }
    } else {
      // Clear cache if forcing re-detection
      try {
        localStorage.removeItem(CURRENCY_CACHE_KEY);
        localStorage.removeItem(CURRENCY_CACHE_TIMESTAMP_KEY);
        console.log('💰 Cache cleared, forcing currency re-detection');
      } catch (error) {
        console.warn('Error clearing currency cache:', error);
      }
    }

    // Detect currency
    setIsDetecting(true);
    try {
      const currency = await detectUserCurrency();
      console.log('💰 Detected user currency:', currency);
      setDetectedCurrency(currency);
      
      // Cache the detected currency
      try {
        localStorage.setItem(CURRENCY_CACHE_KEY, currency);
        localStorage.setItem(CURRENCY_CACHE_TIMESTAMP_KEY, Date.now().toString());
      } catch (error) {
        console.warn('Error caching currency:', error);
      }
    } catch (error) {
      console.error('Error detecting currency:', error);
      // Fallback to USD if detection fails
      setDetectedCurrency('USD');
    } finally {
      setIsDetecting(false);
    }
  };

  const clearCurrencyCache = () => {
    try {
      localStorage.removeItem(CURRENCY_CACHE_KEY);
      localStorage.removeItem(CURRENCY_CACHE_TIMESTAMP_KEY);
      console.log('💰 Currency cache cleared');
      // Trigger re-detection
      detectCurrency(true);
    } catch (error) {
      console.warn('Error clearing currency cache:', error);
    }
  };

  useEffect(() => {
    // Detect currency on mount
    detectCurrency();

    // Listen for currency settings updates (might want to re-detect or clear cache)
    const handleCurrencySettingsUpdate = () => {
      console.log('🔄 Currency settings updated, currency detection may need refresh');
      // Optionally clear cache and re-detect, or just keep current detection
      // For now, we'll keep the current detection as currency settings affect conversion, not detection
    };

    window.addEventListener('currency_settings_updated', handleCurrencySettingsUpdate);

    // Listen for force currency re-detection event
    const handleForceCurrencyDetection = () => {
      console.log('🔄 Force currency re-detection requested');
      detectCurrency(true);
    };

    window.addEventListener('force_currency_detection', handleForceCurrencyDetection);

    return () => {
      window.removeEventListener('currency_settings_updated', handleCurrencySettingsUpdate);
      window.removeEventListener('force_currency_detection', handleForceCurrencyDetection);
    };
  }, []);

  const value: CurrencyContextType = {
    detectedCurrency,
    isDetecting,
    detectCurrency,
    clearCurrencyCache,
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};
